import { getBoardClienteExibicao } from '../utils/clienteBoardDisplay';
import { orderMatchesTipoOSFilter } from './tipoOsFilterUtils';

const ALL_TIPO_LETTERS = ['E', 'B', 'A', 'R', 'D', 'I', 'S', 'C'];

function orderPassesEmAbertoPickerFilters(ordem, itemCidade, filters, skipKey, getTipoVisual) {
  if (skipKey !== 'cidade' && filters.cidade?.length > 0 && !filters.cidade.includes(itemCidade)) {
    return false;
  }
  if (skipKey !== 'bairro' && filters.bairro?.length > 0) {
    const bairro = ordem.TB02115_BAIRRO || '';
    if (!filters.bairro.includes(bairro)) return false;
  }
  if (skipKey !== 'cliente' && filters.cliente?.length > 0) {
    const cliente = getBoardClienteExibicao(ordem) || ordem.cliente || ordem.TB01008_NOME;
    if (!filters.cliente.includes(cliente)) return false;
  }
  if (skipKey !== 'tipoOS' && filters.tipoOS?.length > 0) {
    if (!orderMatchesTipoOSFilter(ordem, filters.tipoOS, getTipoVisual)) return false;
  }
  if (skipKey !== 'sla' && filters.sla?.length > 0) {
    const sla = ordem.sla || 'Em dia';
    if (!filters.sla.includes(sla)) return false;
  }
  if (skipKey !== 'equipamento' && filters.equipamento?.length > 0) {
    const equipamento = ordem.equipamento || ordem.TB01010_RESUMIDO || ordem.TB01010_NOME;
    if (!equipamento || !filters.equipamento.includes(equipamento)) return false;
  }
  if (skipKey !== 'status' && filters.status?.length > 0) {
    const status = ordem.TB01073_NOME || '';
    if (!filters.status.includes(status)) return false;
  }
  if (skipKey !== 'tecnicoInicial' && filters.tecnicoInicial?.length > 0) {
    const tecnicoInicial = ordem.TB01024_NOME || 'SEM TÉCNICO';
    if (!filters.tecnicoInicial.includes(tecnicoInicial)) return false;
  }
  const dstr = (o, key) => String(o[key] ?? '').trim();
  if (skipKey !== 'defeitoDescricao' && filters.defeitoDescricao?.length > 0) {
    const v = dstr(ordem, 'defeito_descricao');
    if (!filters.defeitoDescricao.includes(v)) return false;
  }
  if (skipKey !== 'defeitoAlert' && filters.defeitoAlert?.length > 0) {
    const v = dstr(ordem, 'defeito_alert');
    if (!filters.defeitoAlert.includes(v)) return false;
  }
  if (skipKey !== 'defeitoClassification' && filters.defeitoClassification?.length > 0) {
    const v = dstr(ordem, 'defeito_classification');
    if (!filters.defeitoClassification.includes(v)) return false;
  }
  if (skipKey !== 'defeitoSeverity' && filters.defeitoSeverity?.length > 0) {
    const v = dstr(ordem, 'defeito_severity_level');
    if (!filters.defeitoSeverity.includes(v)) return false;
  }
  return true;
}

export function computeEmAbertoPickerCounts({
  groupedOrders = [],
  filters = {},
  excludeFilterType = null,
  getTipoVisual,
  allowedTipoLetters = null,
}) {
  const skipKey = excludeFilterType || null;
  const tipoLetters = Array.isArray(allowedTipoLetters) && allowedTipoLetters.length > 0
    ? allowedTipoLetters
    : ALL_TIPO_LETTERS;
  const cityData = {};
  const bairroAcc = {};
  const clienteAcc = {};
  const tipoAcc = Object.fromEntries(tipoLetters.map((t) => [t, 0]));
  const slaAcc = {};
  const equipAcc = {};
  const statusAcc = {};
  const tecnicoAcc = {};
  const defeitoAcc = {
    defeitoDescricao: {},
    defeitoAlert: {},
    defeitoClassification: {},
    defeitoSeverity: {},
  };

  groupedOrders.forEach((item) => {
    const cidade = item.cidade;
    if (!cidade || !String(cidade).trim()) return;

    (item.ordens || []).forEach((ordem) => {
      if (!orderPassesEmAbertoPickerFilters(ordem, cidade, filters, skipKey, getTipoVisual)) return;

      const estado = ordem.TB02115_ESTADO || ordem.estado || '';
      if (!cityData[cidade]) cityData[cidade] = { count: 0, estado };
      cityData[cidade].count += 1;

      const bairro = ordem.TB02115_BAIRRO || 'Não informado';
      bairroAcc[bairro] = (bairroAcc[bairro] || 0) + 1;

      const cliente =
        getBoardClienteExibicao(ordem) || ordem.cliente || ordem.TB01008_NOME || 'Não informado';
      clienteAcc[cliente] = (clienteAcc[cliente] || 0) + 1;

      const tipoLetter = getTipoVisual(ordem);
      if (Object.prototype.hasOwnProperty.call(tipoAcc, tipoLetter)) {
        tipoAcc[tipoLetter] += 1;
      }

      const sla = ordem.sla || 'Em dia';
      slaAcc[sla] = (slaAcc[sla] || 0) + 1;

      const equipamento = ordem.equipamento || ordem.TB01010_RESUMIDO || ordem.TB01010_NOME || '';
      if (equipamento) equipAcc[equipamento] = (equipAcc[equipamento] || 0) + 1;

      const status = ordem.TB01073_NOME || '';
      if (status) statusAcc[status] = (statusAcc[status] || 0) + 1;

      const tecnicoInicial = ordem.TB01024_NOME || 'SEM TÉCNICO';
      tecnicoAcc[tecnicoInicial] = (tecnicoAcc[tecnicoInicial] || 0) + 1;

      const dstr = (o, key) => String(o[key] ?? '').trim();
      [
        ['defeitoDescricao', 'defeito_descricao'],
        ['defeitoAlert', 'defeito_alert'],
        ['defeitoClassification', 'defeito_classification'],
        ['defeitoSeverity', 'defeito_severity_level'],
      ].forEach(([accKey, field]) => {
        const v = dstr(ordem, field);
        if (!v) return;
        defeitoAcc[accKey][v] = (defeitoAcc[accKey][v] || 0) + 1;
      });
    });
  });

  const toSortedList = (acc, keyName) =>
    Object.entries(acc)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => ({ [keyName]: key, count }))
      .sort((a, b) => b.count - a.count);

  const buildDefeito = (accKey) =>
    Object.entries(defeitoAcc[accKey])
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value));

  return {
    citiesWithCounts: Object.entries(cityData)
      .filter(([, v]) => v.count > 0)
      .map(([cidade, { count, estado }]) => ({ cidade, count, estado: estado || '' }))
      .sort((a, b) => b.count - a.count),
    bairrosWithCounts: toSortedList(bairroAcc, 'bairro'),
    clientesWithCounts: toSortedList(clienteAcc, 'cliente'),
    tiposOSWithCounts: tipoLetters.map((tipo) => ({ tipo, count: tipoAcc[tipo] || 0 })).filter(
      (item) => item.count > 0
    ),
    slasWithCounts: toSortedList(slaAcc, 'sla'),
    equipamentosWithCounts: toSortedList(equipAcc, 'equipamento'),
    statusWithCounts: toSortedList(statusAcc, 'status'),
    tecnicoInicialWithCounts: toSortedList(tecnicoAcc, 'tecnicoInicial'),
    defeitoDescricaoWithCounts: buildDefeito('defeitoDescricao'),
    defeitoAlertWithCounts: buildDefeito('defeitoAlert'),
    defeitoClassificationWithCounts: buildDefeito('defeitoClassification'),
    defeitoSeverityWithCounts: buildDefeito('defeitoSeverity'),
  };
}
